import {TFunction} from 'i18next';
import {convertVariablesToHtmlInline} from '../../textEditor/Variables';

type FormatCommand = 'bold' | 'italic';

const FORMAT_BY_COMMAND: Record<FormatCommand, {tagName: 'STRONG' | 'EM'; element: 'strong' | 'em'}> = {
    bold: {tagName: 'STRONG', element: 'strong'},
    italic: {tagName: 'EM', element: 'em'},
};

const WHATSAPP_TO_HTML_REPLACEMENTS: Array<[RegExp, string]> = [
    [/(?<!\w)_\*([^*]+)\*_(?!\w)/g, '<strong><em>$1</em></strong>'],
    [/(?<!\w)\*_([^_]+)_\*(?!\w)/g, '<strong><em>$1</em></strong>'],
    [/(?<!\w)\*([^*]+)\*(?!\w)/g, '<strong>$1</strong>'],
    [/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>'],
];

const HTML_TO_WHATSAPP_MARKERS: Array<{tag: 'strong' | 'em'; open: string; close: string}> = [
    {tag: 'strong', open: '*', close: '*'},
    {tag: 'em', open: '_', close: '_'},
];

function getHTMLElement(node: Node): HTMLElement | null {
    return node.nodeType === Node.TEXT_NODE ? node.parentElement : node as HTMLElement;
}

function findInnermostFormatAncestor(element: HTMLElement | null, tagName: 'STRONG' | 'EM'): HTMLElement | null {
    let current: HTMLElement | null = element;
    while(current && current.getAttribute('contenteditable') !== 'true') {
        if(current.tagName === tagName) {
            return current;
        }
        current = current.parentElement;
    }
    return null;
}

function unwrapFormatElement(element: HTMLElement): void {
    const parent = element.parentNode;
    if(!parent) {
        return;
    }
    while(element.firstChild) {
        parent.insertBefore(element.firstChild, element);
    }
    parent.removeChild(element);
}

function restoreSelection(selection: Selection, startNode: Node, endNode: Node): void {
    const range = document.createRange();
    const endOffset = endNode.nodeType === Node.TEXT_NODE
        ? (endNode.textContent?.length ?? 0)
        : endNode.childNodes.length;
    range.setStart(startNode, 0);
    range.setEnd(endNode, endOffset);
    selection.removeAllRanges();
    selection.addRange(range);
}

function normalizeFormatHtmlInPlace(root: Element): void {
    (['strong', 'em'] as const).forEach(tagName => {
        const parentTag = tagName.toUpperCase();
        Array.from(root.querySelectorAll(tagName)).forEach(element => {
            if(element.parentElement?.tagName === parentTag) {
                unwrapFormatElement(element as HTMLElement);
            }
        });
    });
}

function shouldToggleOffFormat(range: Range, element: Element, selectionText: string): boolean {
    const elementRange = document.createRange();
    elementRange.selectNodeContents(element);

    if(range.compareBoundaryPoints(Range.START_TO_START, elementRange) === 0
        && range.compareBoundaryPoints(Range.END_TO_END, elementRange) === 0) {
        return true;
    }

    const selectionCoversElement =
        range.compareBoundaryPoints(Range.START_TO_START, elementRange) >= 0
        && range.compareBoundaryPoints(Range.END_TO_END, elementRange) <= 0;

    return selectionCoversElement
        && selectionText.trim() === (element.textContent ?? '').trim();
}

/**
 * Apply formatting (bold/italic) to currently selected text.
 * Bold and italic can be combined via nested strong/em tags.
 */
export function applyFormatToSelection(command: FormatCommand): boolean {
    const selection = window.getSelection();
    if(!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        return false;
    }

    const range = selection.getRangeAt(0);
    const anchorElement = getHTMLElement(range.commonAncestorContainer);

    if(anchorElement?.closest('.variable')) {
        return false;
    }

    const selectedText = selection.toString();
    if(!selectedText.trim()) {
        return false;
    }

    const {tagName, element: formatTag} = FORMAT_BY_COMMAND[command];
    const existingFormat = findInnermostFormatAncestor(anchorElement, tagName);
    const editableRoot = anchorElement?.closest('[contenteditable="true"]') ?? null;

    const toggleOff = existingFormat
        && existingFormat.contains(range.startContainer)
        && existingFormat.contains(range.endContainer)
        && shouldToggleOffFormat(range, existingFormat, selectedText);

    if(toggleOff) {
        const firstChild = existingFormat.firstChild;
        const lastChild = existingFormat.lastChild;
        unwrapFormatElement(existingFormat);
        if(firstChild && lastChild) {
            restoreSelection(selection, firstChild, lastChild);
        }
    } else {
        const formattedElement = document.createElement(formatTag);
        try {
            const contents = range.extractContents();
            Array.from(contents.querySelectorAll(formatTag)).forEach(nested => {
                unwrapFormatElement(nested as HTMLElement);
            });
            formattedElement.appendChild(contents);
            range.insertNode(formattedElement);

            const newRange = document.createRange();
            newRange.selectNodeContents(formattedElement);
            selection.removeAllRanges();
            selection.addRange(newRange);
        } catch {
            return false;
        }
    }

    if(editableRoot) {
        normalizeFormatHtmlInPlace(editableRoot);
    }
    return true;
}

function trimFormatElementEdges(element: Element): {leading: string; trailing: string} {
    let leading = '';
    let trailing = '';

    while(element.firstChild?.nodeType === Node.TEXT_NODE) {
        const textNode = element.firstChild as Text;
        const match = (textNode.textContent ?? '').match(/^(\s*)([\s\S]*)$/);
        if(!match) break;
        leading += match[1];
        if(match[2].length === 0) {
            element.removeChild(textNode);
            continue;
        }
        textNode.textContent = match[2];
        break;
    }

    while(element.lastChild?.nodeType === Node.TEXT_NODE) {
        const textNode = element.lastChild as Text;
        const match = (textNode.textContent ?? '').match(/^([\s\S]*?)(\s*)$/);
        if(!match) break;
        trailing = match[2] + trailing;
        if(match[1].length === 0) {
            element.removeChild(textNode);
            continue;
        }
        textNode.textContent = match[1];
        break;
    }

    return {leading, trailing};
}

function replaceFormatElementWithWhatsAppMarkers(element: Element, openMarker: string, closeMarker: string): void {
    const {leading, trailing} = trimFormatElementEdges(element);
    const hasContent = (element.textContent ?? '').length > 0;
    const fragment = document.createDocumentFragment();

    if(leading) {
        fragment.appendChild(document.createTextNode(leading));
    }
    if(hasContent) {
        fragment.appendChild(document.createTextNode(openMarker));
        while(element.firstChild) {
            fragment.appendChild(element.firstChild);
        }
        fragment.appendChild(document.createTextNode(closeMarker));
    }
    if(trailing) {
        fragment.appendChild(document.createTextNode(trailing));
    }

    element.parentNode?.replaceChild(fragment, element);
}

export function convertHtmlToWhatsAppFormat(html: string): string {
    if(!html) {
        return '';
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    normalizeFormatHtmlInPlace(tempDiv);

    HTML_TO_WHATSAPP_MARKERS.forEach(({tag, open, close}) => {
        Array.from(tempDiv.querySelectorAll(tag)).reverse().forEach(element => {
            replaceFormatElementWithWhatsAppMarkers(element, open, close);
        });
    });

    return tempDiv.innerHTML;
}

export function convertWhatsAppFormatToHtml(text: string): string {
    if(!text) {
        return '';
    }
    return WHATSAPP_TO_HTML_REPLACEMENTS.reduce(
        (result, [pattern, replacement]) => result.replace(pattern, replacement),
        text,
    );
}

export const convertToHtml = (value: string, defaultVariables: Record<string, string>, t: TFunction) => {
    const htmlContent = convertVariablesToHtmlInline(value, defaultVariables, t);
    return convertWhatsAppFormatToHtml(htmlContent);
};

export function serializeTemplateStructure(structure: any): string {
    if(!structure || !Array.isArray(structure)) return '';
    
    return structure
        .filter(item => item.type !== 'button')
        .map(item => {
            if(item.type === 'inline') {
                let text = item?.value || '';
                if(item.params) {
                    item.params.forEach(param => {
                        if(param.value) {
                            text = text.replace(`{{${param.index}}}`, param.value);
                        }
                    });
                }
                return text;
            }
            if(item.type === 'text') {
                return item.value;
            }
            if(item.type === 'empty') {
                return '';
            }
            return '';
        })
        .join('\n');
}
